import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBillLimit, useClearBillLimit, useUpdateBillLimitNextMonth, useUpdateCurrentBillLimit } from "../hooks";
import Main from "../../_app/layouts/Main";
import StatusChip from "../../_app/components/StatusChip";
import PcrfIndicator from "../components/PcrfIndicator";
import { rowParser } from "../../_app/components/Table/helpers";
import UIConfirm from "../../_app/components/UIConfirm";
import UILoader from "../../_app/components/UILoader";
import { BillLimit as BillLimitType } from "../types";
import { useFeedbackAlerts, useStore } from "../../_app/hooks";
import { formatCurrency, to2Digits } from "../../_app/utils/format";
import { useHasAnyFeatureEnabled } from "../../feature/hooks";
import { useValidation } from "../../form/hooks";
import { createStylesheet } from "../../_app/utils/styles";
import { Close as CloseIcon } from "../../_app/components/icons";
import {
  UIAlert,
  UIButton,
  UICard,
  UIFormControl,
  UIFormHelperText,
  UIGrid,
  UIIconButton,
  UIInputAdornment,
  UIMenuItem,
  UISelect,
  UITextField,
  UITooltip,
  UITypography,
} from "../../_app/components";
import UIDataTable, { UIDataTableState } from "../../_app/components/Table/UIDataTable";
import { Feature } from "../../feature/types";
import { UserType } from "../../user-level/types";

const PCRF_ALLOWED_BILL_LIMITS = ["0", "5", "10", "15", "20", "30", "50", "75", "100", "200"];
const ACTIONS_REQUIRING_BILL_LIMIT_INPUT = new Set(["set-next-month-limit", "set-current-limit"]);

export const BillLimit = () => {
  const location = useLocation();
  const classes = useStyles();
  const { dispatch } = useStore();
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const rowCountOptions = [25, 50, 100];
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(rowCountOptions[0]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const navigate = useNavigate();
  const assets = location.state?.assets;
  const filters = location.state?.filters;

  useEffect(() => {
    if (!assets?.length && !filters) {
      setFeedbackAlertError("No eligible mobile numbers selected");
      navigate({
        pathname: "/mobile-management",
      });
    }
  }, [assets, dispatch, filters, navigate, setFeedbackAlertError]);

  // Numbers removed by the user directly from this screen's table, so they're excluded
  // from both the displayed table and the operation submitted to the API. Only possible
  // when an explicit list of assets was passed in - a filter-based ("select all") selection
  // has no client-side id list to exclude from.
  const [removedAssetIds, setRemovedAssetIds] = useState<Set<string>>(new Set());
  const canRemoveRows = Boolean(assets?.length) && !filters;
  const activeAssets = canRemoveRows ? assets.filter((a: any) => !removedAssetIds.has(a?.id)) : assets;

  const handleRemoveRow = (assetId?: string) => {
    if (!assetId || activeAssets?.length <= 1) return;
    setRemovedAssetIds((prev) => new Set(prev).add(assetId));
  };

  // Bill Limit Value
  const [billLimitValue, setNewBillLimit] = useState("");
  const validationConfig: any = {
    billLimit: {
      required: true,
      validate: (val: any) => {
        const regex = /^\d+(\.\d{0,2})?$/;
        if (val < 0) return "Cannot be less that 0";
        if (val > 999999.99) return "Exceeded maximum limit";
        if (!regex.test(val)) return "Has to be a 2 decimal number";
      },
    },
  };
  const { validationErrors, validateSingle } = useValidation(validationConfig, {
    billLimitValue,
  });

  const [selectedActionId, setSelectedActionId] = useState<string>("set-next-month-limit");
  const isCurrentLimitAction = selectedActionId === "set-current-limit";
  const requiresBillLimitInput = ACTIONS_REQUIRING_BILL_LIMIT_INPUT.has(selectedActionId);

  const { data: assetsResp, isFetching } = useBillLimit(
    activeAssets?.map((a: any) => a?.id),
    page,
    rowCount,
    filters,
  );

  // Setting a limit requires knowing every selected number, not just the ones on the
  // currently displayed page: "Update immediately" checks each number's current bill limit
  // against the new value, and any PCRF-enrolled number restricts the allowed values.
  const { data: allBillLimitsResp, isFetching: isFetchingAllBillLimits } = useBillLimit(
    activeAssets?.map((a: any) => a?.id),
    0,
    assetsResp?.totalElements || 0,
    filters,
    { enabled: requiresBillLimitInput && !!assetsResp?.totalElements },
  );

  const hasPcrfManaged = Boolean(allBillLimitsResp?.content?.some((item) => item.pcrfManaged));

  // "Update immediately" (ST-33901) requires the new limit to be higher than every number's
  // current limit, so options that could never pass that rule are not offered at all.
  const currentLimits = (allBillLimitsResp?.content || [])
    .map((item) => item.current)
    .filter((value): value is number => value != null);
  const maxCurrentLimit = isCurrentLimitAction && currentLimits.length ? Math.max(...currentLimits) : null;
  const pcrfAllowedLimits = useMemo(
    () =>
      maxCurrentLimit == null
        ? PCRF_ALLOWED_BILL_LIMITS
        : PCRF_ALLOWED_BILL_LIMITS.filter((value) => Number(value) > maxCurrentLimit),
    [maxCurrentLimit],
  );

  // A stale value can survive a change of input mode or action (the PCRF flag arrives async,
  // and the offered options shrink for "Update immediately"), where it would be submittable
  // without matching any offered option.
  useEffect(() => {
    if (hasPcrfManaged && billLimitValue && !pcrfAllowedLimits.includes(billLimitValue)) {
      setNewBillLimit("");
    }
  }, [hasPcrfManaged, billLimitValue, pcrfAllowedLimits]);

  const { mutate: executeClearBillLimit, isPending: isClearing } = useClearBillLimit({
    onSuccess: () => {
      navigate(-1);
    },
  });

  const { mutate: executeNextMonthBillLimitUpdate, isPending: isUpdatingNextMonth } = useUpdateBillLimitNextMonth({
    onSuccess: () => {
      navigate(-1);
    },
  });

  const { mutate: executeCurrentBillLimitUpdate, isPending: isUpdatingCurrent } = useUpdateCurrentBillLimit({
    onSuccess: () => {
      navigate(-1);
    },
  });

  const actionList = [
    {
      id: "clear-limits",
      enabled: useHasAnyFeatureEnabled(Feature.BillLimit),
      value: "Clear limits",
      dialogTitle: "Clear limits Confirmation",
      dialogText: [
        <UITypography variant="body2" gutterBottom key="warning1">
          Please carefully consider removing bill limit(s).
        </UITypography>,
        <UITypography variant="body2" gutterBottom key="warning2">
          The bill limit cannot be re-instated for the current month once removed.
        </UITypography>,
        <UITypography variant="body2" key="warning3">
          Any capped charges will become chargeable upon removal of the bill limit.
        </UITypography>,
      ],
      canSubmit: () => !isLoading,
      action: () => {
        executeClearBillLimit({
          assetIds: activeAssets?.map((a: any) => a?.id),
          filters,
        });
      },
    },
    {
      id: "set-next-month-limit",
      enabled: useHasAnyFeatureEnabled(Feature.BillLimit),
      value: "Set limit from next month",
      dialogTitle: "Set limit from the 1st of next month",
      dialogText: [
        <UITypography variant="body2" gutterBottom key="warning4">
          The new bill limit will come into effect from the 1st of next month. <br />
          By submitting this request, you agree and acknowledge the following:
        </UITypography>,
        <ul key="warning5">
          <li>
            <UITypography variant="body2" gutterBottom>
              Where necessary some services will be restricted to minimise the risk of further cost - Emergency services will not
              be affected by any applicable restrictions.
            </UITypography>
          </li>
          <li>
            <UITypography variant="body2" gutterBottom>
              Calls that carry a third-party service, such as directory enquiries and other non-geographical numbers, will still
              be charged. Only network access fees will be limited.
            </UITypography>
          </li>
          <li>
            <UITypography variant="body2" gutterBottom>
              MPAY and other non-mobile service charges are excluded completely from the Bill Limit service and these will remain
              chargeable in full.
            </UITypography>
          </li>
        </ul>,
        <UITypography variant="body2" key="warning6">
          In the event you have concerns with the terms of this service, please be aware that we offer an alternative and highly
          adaptive usage alert cost management service that may be suitable for your requirements.
        </UITypography>,
      ],
      canSubmit: () => {
        return !isLoading && !isFetchingAllBillLimits && !validationErrors.billLimit && billLimitValue;
      },
      action: () => {
        if (!validationErrors.billLimit && billLimitValue) {
          executeNextMonthBillLimitUpdate({
            assetIds: activeAssets?.map((a: any) => a?.id),
            filters,
            operations: [{ limit: billLimitValue as unknown as number }],
          });
        }
      },
    },
    {
      id: "set-current-limit",
      enabled: useHasAnyFeatureEnabled(Feature.BillLimit),
      value: "Update immediately",
      dialogTitle: "Increase Current Period Bill Limit Confirmation",
      dialogText: [
        <UITypography variant="body2" gutterBottom key="warning4">
          Please carefully consider increasing bill limit(s) for the current period. <br />
          <br />
          The bill limit increase cannot be reversed for the current month once increased. <br />
          <br />
          Any capped charges up to the new limit will become chargeable upon increase of the bill limit. <br />
          <br />
        </UITypography>,
      ],
      canSubmit: () => {
        const items = allBillLimitsResp?.content || [];
        const allHaveCurrentLimit = items.length > 0 && items.every((item) => item.current != null);
        const newLimit = Number(billLimitValue);
        const isHigherThanAllCurrentLimits = items.every((item) => newLimit > (item.current as number));
        return (
          !isLoading &&
          !isFetchingAllBillLimits &&
          !validationErrors.billLimit &&
          !!billLimitValue &&
          allHaveCurrentLimit &&
          isHigherThanAllCurrentLimits
        );
      },
      getDisabledReason: () => {
        const items = allBillLimitsResp?.content || [];
        if (isFetchingAllBillLimits || !items.length) return undefined;
        if (items.some((item) => item.current == null)) {
          return "A bill limit cannot be set in the current period for number(s) that have opted out of bill limits. They can only be applied from next month. Please change the option to ‘Set limit from next month’ and try again.";
        }
        if (hasPcrfManaged && !pcrfAllowedLimits.length) {
          return "No permitted PCRF bill limit value is higher than the current limit. Please change the option to ‘Set limit from next month’ and try again.";
        }
        if (billLimitValue && !validationErrors.billLimit) {
          const newLimit = Number(billLimitValue);
          if (items.some((item) => newLimit <= (item.current as number))) {
            return "The new limit must be higher than the current limit for every number.";
          }
        }
        return undefined;
      },
      action: () => {
        if (!validationErrors.billLimit && billLimitValue) {
          executeCurrentBillLimitUpdate({
            assetIds: activeAssets?.map((a: any) => a?.id),
            filters,
            operations: [{ limit: billLimitValue as unknown as number }],
          });
        }
      },
    },
  ];
  const defaultAction = actionList[1] || actionList[0];
  const getActionFromActionId = (id: string) => actionList?.find((a) => a?.id === id);
  const getActionFromActionValue = (val: string) => actionList?.find((a) => a?.value === val);

  const selectedAction = getActionFromActionId(selectedActionId);
  const isMissingCurrentLimitForCurrentAction =
    isCurrentLimitAction && (allBillLimitsResp?.content || []).some((item) => item.current == null);
  const billLimitFieldError = validationErrors.billLimit || selectedAction?.getDisabledReason?.();

  const handleActionChange = (event: any) => {
    const action = getActionFromActionValue(event.target.value);
    setSelectedActionId(action?.id || "");
  };

  const handleSubmit = () => {
    if (selectedAction?.canSubmit()) setOpenDialog(true);
  };

  const handleTableChange = (action: string, state: UIDataTableState) => {
    switch (action) {
      case "changePage":
        setPage(state.page);
        break;
      case "changeRowsPerPage":
        setRowCount(state.rowsPerPage);
        break;
      default:
        break;
    }
  };

  const renderCurrentLimitCell = (item: BillLimitType) => {
    const currentLimitText = item.current ? formatCurrency(item.current) : "";
    if (selectedActionId !== "set-current-limit") return currentLimitText;

    if (item.current == null) {
      return (
        <UITooltip title="No current bill limit is set for this number">
          <UITypography variant="body2" color="error">
            No limit set
          </UITypography>
        </UITooltip>
      );
    }

    if (billLimitValue && !validationErrors.billLimit && Number(billLimitValue) <= item.current) {
      return (
        <UITooltip title="The new limit must be higher than this number's current limit">
          <UITypography variant="body2" color="error">
            {currentLimitText}
          </UITypography>
        </UITooltip>
      );
    }

    return currentLimitText;
  };

  const parsed =
    assetsResp?.content?.map((item: BillLimitType) => {
      const row: any[] = [
        <span className={classes.cliCell} key="cli">
          {item.cli}
          {item.pcrfManaged && <PcrfIndicator cli={item.cli} />}
        </span>,
        !item.eligible ? <StatusChip status="FAILED" label="Not Eligible" /> : " ",
        item.tag,
        renderCurrentLimitCell(item),
        item.nextMonth ? formatCurrency(item.nextMonth) : "",
      ];
      if (canRemoveRows) {
        const isLastRemainingRow = (activeAssets?.length || 0) <= 1;
        row.push(
          <UITooltip title={isLastRemainingRow ? "At least one number must remain" : "Remove this number"} key="remove">
            <span>
              <UIIconButton
                size="small"
                aria-label="remove"
                onClick={() => handleRemoveRow(item.assetId)}
                disabled={isLastRemainingRow}
                data-cy={`remove-row-${item.cli}`}
              >
                <CloseIcon fontSize="small" />
              </UIIconButton>
            </span>
          </UITooltip>,
        );
      }
      return rowParser(row);
    }) || [];

  const renderError = () => {
    if (assets?.length) return null;
    if (filters) return null;
    return <UIAlert severity="error">No mobile numbers selected</UIAlert>;
  };

  const isLoading = isFetching || isUpdatingNextMonth || isUpdatingCurrent || isClearing;

  return (
    <Main
      title="Bill Limit"
      data-cy="bill-limit-page"
      needSelectedAccount={false}
      feature={Feature.BillLimit}
      showNav={false}
      showToolbar={false}
      showFooter={false}
      allowedUserTypes={[UserType.COST_CENTRE, UserType.ACCOUNT]}
    >
      {renderError() || (
        <UIGrid size={{ xs: 12, md: 12 }}>
          <UIGrid container>
            <div className={classes.actionItem}>
              <UIAlert severity="warning" className={classes.alert}>
                This service allows you to set a bill limit (£) against each individual mobile number. Bill limits exclude line
                rentals.
                <ul>
                  <li>By setting a Zero ("0") in this field, additional chargeable usage will be barred.</li>
                  <li>
                    By selecting clear limits in this field, you are opting out of a Bill Limit, meaning additional chargeable
                    usage will apply.
                  </li>
                </ul>
              </UIAlert>
            </div>
            <UICard className={classes.actionsCtr}>
              <UIFormControl variant="outlined" size="small">
                <UISelect name="actionSelector" defaultValue={defaultAction?.value} onChange={handleActionChange}>
                  {actionList.reduce((result: any[], a) => {
                    if (a.enabled && a.value) {
                      result.push(
                        <UIMenuItem key={a.id} value={a.value}>
                          {a.value}
                        </UIMenuItem>,
                      );
                      return result;
                    }
                    return result;
                  }, [])}
                </UISelect>
              </UIFormControl>
              {requiresBillLimitInput &&
                (hasPcrfManaged ? (
                  <UIFormControl
                    variant="outlined"
                    size="small"
                    className={classes.billLimitField}
                    error={Boolean(billLimitFieldError)}
                  >
                    <UISelect
                      name="billLimit"
                      value={billLimitValue}
                      displayEmpty
                      renderValue={(selected) => (selected ? `£${selected}` : "Value")}
                      onChange={(e) => {
                        const val = e.target.value as string;
                        setNewBillLimit(val);
                        validateSingle("billLimit", val);
                      }}
                      disabled={isMissingCurrentLimitForCurrentAction || !pcrfAllowedLimits.length}
                      data-cy="bill-limit-select"
                    >
                      {pcrfAllowedLimits.map((value) => (
                        <UIMenuItem key={value} value={value}>
                          £{value}
                        </UIMenuItem>
                      ))}
                    </UISelect>
                    {billLimitFieldError && <UIFormHelperText>{billLimitFieldError}</UIFormHelperText>}
                  </UIFormControl>
                ) : (
                  <UITextField
                    name="billLimit"
                    placeholder="Value"
                    value={billLimitValue}
                    className={classes.billLimitField}
                    InputProps={{
                      startAdornment: <UIInputAdornment position="start">£</UIInputAdornment>,
                    }}
                    onChange={(e) => {
                      const val = to2Digits(e.target.value);
                      setNewBillLimit(val);
                      validateSingle(e.target.name, val);
                    }}
                    variant="outlined"
                    size="small"
                    error={Boolean(billLimitFieldError)}
                    helperText={billLimitFieldError}
                    disabled={isMissingCurrentLimitForCurrentAction}
                  />
                ))}
              {hasPcrfManaged && requiresBillLimitInput && (
                <UIAlert severity="info" className={classes.pcrfInfo} data-cy="pcrf-info">
                  At least one number selected is enrolled onto PCRF and therefore the bill limit values are restricted to these
                  option.
                </UIAlert>
              )}
              <UIButton
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleSubmit}
                className={classes.submit}
                disabled={!selectedAction?.canSubmit()}
                data-cy="submit-btn"
              >
                Submit
              </UIButton>
            </UICard>
            <UIConfirm
              open={openDialog}
              setOpen={setOpenDialog}
              onConfirm={selectedAction?.action}
              title={selectedAction?.dialogTitle}
              onSubmitTitle="Confirm"
              width="sm"
            >
              {selectedAction?.dialogText}
            </UIConfirm>
          </UIGrid>
          {isLoading ? (
            <div className={classes.loader}>
              <UILoader />
            </div>
          ) : (
            <div data-cy="mobile-selection-table">
              <UIDataTable
                title=""
                data={parsed}
                columns={
                  canRemoveRows
                    ? ["CLI/Identifier", "", "Tag", "Current Limit", "Next Month", ""]
                    : ["CLI/Identifier", "", "Tag", "Current Limit", "Next Month"]
                }
                options={{
                  page,
                  rowsPerPage: rowCount,
                  rowsPerPageOptions: rowCountOptions,
                  onTableChange: handleTableChange,
                  count: assetsResp?.totalElements,
                  pagination: true,
                  download: false,
                  elevation: 1,
                  print: false,
                  responsive: "standard",
                  selectableRows: "none",
                  serverSide: true,
                  selectToolbarPlacement: "none",
                  filter: false,
                  viewColumns: false,
                  sort: false,
                  search: false,
                  rowHover: true,
                  setTableProps: () => ({ size: "medium" }),
                  setRowProps: (row, dataIndex) => ({ "data-cy": `row-id-${assetsResp?.content?.[dataIndex]?.cli}` }),
                }}
              />
            </div>
          )}
        </UIGrid>
      )}
    </Main>
  );
};

const useStyles = createStylesheet((theme) => ({
  loader: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "calc(50vh - 200px)",
  },
  actionsCtr: {
    padding: "10px",
    marginTop: "15px",
    marginBottom: "10px",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    width: "100%",
  },
  alert: {
    width: "100%",
    marginBottom: "5px",
    "& img": {
      objectFit: "contain",
      width: theme.spacing(2),
      margin: "0px 4px -2px",
    },
    [theme.breakpoints.down("md")]: {
      "& .MuiAlert-message": {
        fontSize: "0.7rem",
      },
      "& img": {
        width: "13px",
      },
    },
  },
  actionItem: {
    width: "100%",
  },
  submit: {
    marginTop: 2,
  },
  billLimitField: {
    width: 260,
  },
  pcrfInfo: {
    flex: 1,
    padding: "0px 12px",
  },
  cliCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

export default BillLimit;
